/**
 * fish-table.js — bulk-order controller for the fish collection table
 * (sections/fish-collection.liquid). One qty input per variant row; a sticky
 * bar syncs the cart's fish lines to the displayed quantities in one action
 * via PondKing.Gate.syncFish (fish-only cart rules + mix modal + diffing).
 *
 * The table reflects the cart: on load the current fish lines are pulled from
 * /cart.js and seeded into the inputs, so a reload (or a return from the cart)
 * shows what the customer already has, editing a quantity adjusts that line,
 * and zeroing a carted row removes it — no double-adds.
 *
 * Selections (desired quantities) live in memory AND sessionStorage keyed by
 * variant id, so they survive both the 45s inventory swaps of
 * #FishAjaxContent (fish-inventory.js) and pagination — pick fish on page 1
 * and page 2, then sync once. Rows carry data-variant-id, which distinguishes
 * "rendered here but sold out" (reconcile with the cart + tell the customer)
 * from "lives on another page" (keep it). Off-page stock: /cart/add.js
 * validates additions (all-or-nothing), but /cart/update.js does NOT validate
 * quantity raises on already-carted lines — Shopify checkout is the hard
 * backstop there, and the cart-page banners explain.
 *
 * The add bar and the notice/error lines sit OUTSIDE the swap node and are
 * only ever touched here.
 */
(function () {
  'use strict';

  window.PondKing = window.PondKing || {};
  if (window.PondKing.FishTable) return;

  var STORAGE_KEY = 'pk-fish-selections';
  var strings = (window.PondKingFish && window.PondKingFish.strings) || {};

  var root = null;
  var listenersBound = false;
  var cartBaseline = {}; // variantId -> qty of fish lines in the cart at seed time
  var selections = loadStored(); // variantId -> { q: desired qty, p: price cents }

  function loadStored() {
    try {
      var parsed = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY));
      if (parsed && typeof parsed === 'object') {
        // Keep only well-formed {q, p} entries — corrupt/foreign data must
        // never poison the totals math or the sync payload. q of 0 is valid:
        // it records "remove this carted fish".
        var clean = {};
        for (var id in parsed) {
          if (!Object.prototype.hasOwnProperty.call(parsed, id)) continue;
          var entry = parsed[id];
          if (entry && typeof entry.q === 'number' && entry.q >= 0 &&
              isFinite(entry.q) && typeof entry.p === 'number' && isFinite(entry.p)) {
            clean[id] = { q: Math.floor(entry.q), p: Math.floor(entry.p) };
          }
        }
        return clean;
      }
    } catch (err) { /* private mode / corrupt JSON — start clean */ }
    return {};
  }

  function persist() {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selections));
    } catch (err) { /* private mode — in-memory only */ }
  }

  function clearSelections() {
    selections = {};
    try { window.sessionStorage.removeItem(STORAGE_KEY); } catch (err) { /* noop */ }
  }

  function el(selector) {
    return root ? root.querySelector(selector) : null;
  }

  function qtyInputs() {
    return root ? root.querySelectorAll('input[data-fish-qty]') : [];
  }

  function formatMoney(cents) {
    if (window.theme && window.theme.Currency && window.theme.settings) {
      return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function clampedValue(input) {
    var value = parseInt(input.value, 10);
    if (isNaN(value) || value < 0) value = 0;
    var max = parseInt(input.getAttribute('max'), 10);
    if (!isNaN(max) && value > max) value = max;
    return value;
  }

  function rememberInput(input, forceClamp) {
    var id = input.getAttribute('data-variant-id');
    if (!id) return;
    var value = clampedValue(input);
    // Don't fight the keyboard: only rewrite the field when the typed value
    // is genuinely out of bounds, or when a final clamp is requested.
    if (forceClamp || (input.value !== '' && String(input.value) !== String(value))) {
      input.value = value;
    }
    if (value > 0) {
      selections[id] = { q: value, p: parseInt(input.getAttribute('data-price'), 10) || 0 };
    } else if (cartBaseline[id] > 0) {
      // Zeroing a carted fish = remove it from the cart on sync.
      selections[id] = { q: 0, p: parseInt(input.getAttribute('data-price'), 10) || 0 };
    } else {
      delete selections[id];
    }
    persist();
  }

  function selectionState() {
    var count = 0;
    var subtotal = 0;
    var removals = false;
    for (var id in selections) {
      if (!Object.prototype.hasOwnProperty.call(selections, id)) continue;
      if (selections[id].q === 0) { removals = true; continue; }
      count += selections[id].q;
      subtotal += selections[id].q * selections[id].p;
    }
    return { count: count, subtotal: subtotal, removals: removals };
  }

  function messageVisible() {
    var notice = el('[data-fish-adjust-notice]');
    var error = el('[data-fish-add-error]');
    return (notice && !notice.classList.contains('hide')) ||
      (error && !error.classList.contains('hide'));
  }

  // Online fish orders are capped (Theme settings → Live Fish Ordering;
  // 0 disables). Larger orders are scheduled deliveries arranged by phone.
  function overOrderLimit(subtotal) {
    if (!root) return false;
    var max = parseInt(root.getAttribute('data-fish-max-total'), 10) || 0;
    return max > 0 && subtotal >= max;
  }

  function orderLimitMessage() {
    return (root && root.getAttribute('data-fish-max-message')) ||
      strings.overLimit || 'This order is over our online limit — please call the office to schedule a delivery.';
  }

  function updateSummary() {
    var bar = el('[data-fish-add-bar]');
    if (!bar) return;
    var state = selectionState();
    var summary = el('[data-fish-selected-summary]');
    if (summary) {
      var template = strings.tableSelected || '[count] fish selected';
      summary.textContent = (state.count > 0 || state.removals)
        ? template.replace('[count]', String(state.count)) + ' · ' + formatMoney(state.subtotal)
        : '';
    }
    var overLimit = overOrderLimit(state.subtotal);
    // Direct DOM update — showMessage() calls back into updateSummary, so
    // using it here would recurse.
    var limitNode = el('[data-fish-limit-notice]');
    if (limitNode) {
      limitNode.textContent = overLimit ? orderLimitMessage() : '';
      limitNode.classList.toggle('hide', !overLimit);
    }
    var syncable = state.count > 0 || state.removals;
    // The messages live inside the bar — keep it visible while one is up,
    // even when the selection just vanished (that's exactly when the notice
    // needs to be read).
    bar.classList.toggle('hide', !syncable && !messageVisible());
    var button = el('[data-fish-add-button]');
    if (button && !button.classList.contains('btn--loading')) button.disabled = !syncable || overLimit;
  }

  // Locale strings edited through Shopify's language editor can arrive with
  // HTML entities (&#39; etc.) baked in; these messages render via textContent,
  // which shows entities literally. Same innerHTML->textContent normalization
  // as fish-cart-gate's stripHtml.
  function toPlainText(text) {
    if (!text || text.indexOf('&') === -1) return text;
    var tmp = document.createElement('div');
    tmp.innerHTML = text;
    return tmp.textContent || '';
  }

  function showMessage(selector, message) {
    var node = el(selector);
    if (!node) return;
    if (message) {
      node.textContent = toPlainText(message);
      node.classList.remove('hide');
    } else {
      node.textContent = '';
      node.classList.add('hide');
    }
    updateSummary(); // bar visibility depends on visible messages
  }

  /**
   * Pull the cart's fish lines into the table state: carted fish without a
   * pending edit are seeded at their cart quantity, so the table always shows
   * (at least) what the customer already has.
   */
  function seedFromCart() {
    if (!window.PondKing.Gate || !window.PondKing.Gate.fetchCart) return Promise.resolve();
    var fishType = (window.PondKingFish && window.PondKingFish.fishType) || 'Live Animals';
    return window.PondKing.Gate.fetchCart()
      .then(function (cart) {
        // Keep the attestation checkbox honest with the cart's actual record
        // (bfcache restores a previous order's tick; another tab may clear) —
        // unless the user already touched it this page-view: their in-flight
        // tick must not be reverted by this fetch's earlier snapshot.
        var attest = document.querySelector('[data-fish-attest]');
        if (attest && attest.dataset.fishAttestTouched !== '1') {
          attest.checked = !!(cart.attributes && cart.attributes['Drive Time Confirmed']);
        }
        var agree = document.querySelector('[data-fish-agree]');
        if (agree && agree.dataset.fishAgreeTouched !== '1') {
          agree.checked = !!(cart.attributes && cart.attributes['Reservation Policy Agreed']);
        }
        cartBaseline = {};
        (cart.items || []).forEach(function (item) {
          if (item.product_type !== fishType) return;
          var id = String(item.variant_id);
          cartBaseline[id] = (cartBaseline[id] || 0) + item.quantity;
          if (!Object.prototype.hasOwnProperty.call(selections, id)) {
            selections[id] = { q: cartBaseline[id], p: item.price || 0 };
          }
        });
        // Stale removal markers for fish no longer in the cart are meaningless.
        for (var id2 in selections) {
          if (Object.prototype.hasOwnProperty.call(selections, id2) &&
              selections[id2].q === 0 && !cartBaseline[id2]) {
            delete selections[id2];
          }
        }
        persist();
      })
      .catch(function () { /* offline — the table still works for picking */ });
  }

  /**
   * Sync the freshly rendered page with the pending selections: re-apply
   * quantities to this page's inputs (clamped to new stock), prune selections
   * whose row renders here as sold out, and leave other-page selections alone.
   */
  function restoreSelections() {
    if (!root) return;
    var adjusted = false;
    var rows = root.querySelectorAll('tr[data-variant-id]');
    for (var i = 0; i < rows.length; i++) {
      var id = rows[i].getAttribute('data-variant-id');
      var input = rows[i].querySelector('input[data-fish-qty]');
      var entry = Object.prototype.hasOwnProperty.call(selections, id) ? selections[id] : null;
      if (!input) {
        // Rendered here but sold out. A carted quantity is still real (the
        // line exists in the cart) — mirror it silently so every load doesn't
        // replay a bogus "quantities updated" notice; only quantities staged
        // ABOVE what's carted get dropped, with the notice. A pending removal
        // (q:0) stays: sync still needs to drop that line.
        var base = cartBaseline[id] || 0;
        if (entry && entry.q !== base) {
          if (base > 0) selections[id] = { q: base, p: entry.p };
          else delete selections[id];
          if (entry.q > 0) adjusted = true;
        }
        continue;
      }
      // The focused field is user territory: fish-inventory.js just restored
      // its exact value (possibly deliberately cleared mid-edit) — writing
      // '0' under the cursor would corrupt the next keystroke.
      if (input === document.activeElement) continue;
      if (!entry || entry.q === 0) { input.value = 0; continue; }
      input.value = entry.q;
      var value = clampedValue(input);
      if (value !== entry.q) adjusted = true; // stock dropped below the selection
      input.value = value;
      if (value > 0 || cartBaseline[id] > 0) {
        selections[id] = { q: value, p: parseInt(input.getAttribute('data-price'), 10) || entry.p };
      } else {
        delete selections[id];
      }
    }
    persist();
    showMessage('[data-fish-adjust-notice]', adjusted
      ? (strings.tableAdjusted || 'Availability just changed — your quantities were updated.')
      : null);
    updateSummary();
  }

  /**
   * "Start with a package": replace the current selections with a preset's
   * lines ([{id, q, p, max}] from fish-preset-picker.liquid — max '' means
   * uncapped). Carted fish not in the preset become explicit removals (q:0)
   * so the sticky-bar sync drops them; nothing touches the cart here.
   * Quantities are clamped to the render-time max now and re-clamped against
   * live stock by restoreSelections on the next swap.
   */
  function applyPreset(items) {
    if (!root || !items || !items.length) return;
    var keep = {};
    var clamped = false;
    items.forEach(function (item) {
      var id = String(item.id);
      keep[id] = true;
      var want = Math.max(0, parseInt(item.q, 10) || 0);
      var max = parseInt(item.max, 10);
      if (!isNaN(max) && want > max) {
        want = Math.max(0, max);
        clamped = true;
      }
      if (want > 0) {
        selections[id] = { q: want, p: parseInt(item.p, 10) || 0 };
      } else if (cartBaseline[id] > 0) {
        selections[id] = { q: 0, p: parseInt(item.p, 10) || 0 };
      } else {
        delete selections[id];
      }
    });
    for (var id in selections) {
      if (!Object.prototype.hasOwnProperty.call(selections, id) || keep[id]) continue;
      if (cartBaseline[id] > 0) selections[id].q = 0;
      else delete selections[id];
    }
    persist();
    restoreSelections(); // paints the visible inputs (and re-clamps them)
    if (clamped) {
      showMessage('[data-fish-adjust-notice]', strings.presetAdjusted ||
        'Some fish in this package are low on stock — quantities were set to what’s available.');
    }
    var table = el('#FishAjaxContent');
    if (table && table.scrollIntoView) table.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function presetItemsFor(button) {
    try {
      var items = JSON.parse(button.getAttribute('data-fish-preset-items'));
      return Array.isArray(items) ? items : null;
    } catch (err) { return null; }
  }

  /* ------------- pre-checkout confirmation modal ------------- */

  var statusModal = null;
  var statusState = null;
  var statusOnClose = null;
  var statusRedirectTimer = null;

  function closeStatusModal() {
    // Always kill the pending auto-redirect: a bfcache-restored page must
    // never resume a suspended timer and yank the customer back to checkout.
    clearTimeout(statusRedirectTimer);
    statusRedirectTimer = null;
    if (statusModal && statusModal.parentNode) statusModal.parentNode.removeChild(statusModal);
    statusModal = null;
    statusState = null;
    statusOnClose = null;
    document.removeEventListener('keydown', onStatusKeydown);
  }

  // User-initiated dismissal (Close button, Escape, overlay click): restores
  // focus to the bar button and, when a completed sync is being abandoned
  // ("stay on this page"), unlocks the button and reseeds from the cart.
  function dismissStatusModal() {
    var state = statusState;
    var onClose = statusOnClose;
    closeStatusModal();
    var barButton = el('[data-fish-add-button]');
    if (state === 'success') {
      if (barButton) barButton.classList.remove('btn--loading');
      seedFromCart().then(restoreSelections); // the sync DID apply — mirror it
    }
    if (barButton && barButton.focus) {
      try { barButton.focus({ preventScroll: true }); } catch (err) { barButton.focus(); }
    }
    if (onClose) onClose();
  }

  function onStatusKeydown(evt) {
    if (!statusModal) return;
    if (evt.key === 'Escape') {
      if (statusState === 'checking') return; // resolves in a moment
      evt.preventDefault();
      dismissStatusModal();
    } else if (evt.key === 'Tab') {
      // Minimal focus trap: the dialog has at most one control.
      evt.preventDefault();
      var btn = statusModal.querySelector('button');
      if (btn) btn.focus();
    }
  }

  /**
   * One modal, three states:
   *  'checking' — "confirming inventory and your pickup time…" (no dismiss;
   *               the checks resolve in a few seconds)
   *  'success'  — confirmed + "complete checkout within 5 minutes", with a
   *               Go-now button and a cancellable auto-redirect (set by the
   *               caller via statusRedirectTimer)
   *  'error'    — what failed (message) + what to do next (nextStep) + Close
   */
  function showStatusModal(state, message, nextStep, onClose) {
    message = toPlainText(message);
    nextStep = toPlainText(nextStep);
    closeStatusModal();
    statusState = state;
    statusOnClose = onClose || null;
    statusModal = document.createElement('div');
    statusModal.setAttribute('role', state === 'error' ? 'alertdialog' : 'dialog');
    statusModal.setAttribute('aria-modal', 'true');
    statusModal.setAttribute('aria-label', message);
    statusModal.style.cssText =
      'position:fixed;inset:0;z-index:100000;display:flex;align-items:center;' +
      'justify-content:center;background:rgba(0,0,0,0.55);padding:20px;';

    var box = document.createElement('div');
    box.style.cssText =
      'background:#fff;color:#1c1d1d;max-width:440px;width:100%;border-radius:6px;' +
      'padding:26px;box-shadow:0 10px 40px rgba(0,0,0,0.3);text-align:center;';

    var text = document.createElement('p');
    text.style.cssText = 'font-weight:700;font-size:1.1em;margin:0 0 8px;';
    text.textContent = message;
    box.appendChild(text);

    if (nextStep) {
      var next = document.createElement('p');
      next.style.cssText = 'margin:0 0 16px;';
      next.textContent = nextStep;
      box.appendChild(next);
    }

    if (state === 'success') {
      var goBtn = document.createElement('button');
      goBtn.type = 'button';
      goBtn.className = 'btn';
      goBtn.textContent = strings.confirmGoNow || 'Go to checkout now';
      goBtn.addEventListener('click', function () {
        clearTimeout(statusRedirectTimer);
        statusRedirectTimer = null;
        goBtn.classList.add('btn--loading');
        window.location.href = '/checkout';
      });
      box.appendChild(goBtn);
      setTimeout(function () { goBtn.focus(); }, 0);
    } else if (state === 'error') {
      var closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'btn btn--secondary';
      closeBtn.textContent = strings.confirmClose || 'Close';
      closeBtn.addEventListener('click', dismissStatusModal);
      box.appendChild(closeBtn);
      setTimeout(function () { closeBtn.focus(); }, 0);
    }

    statusModal.appendChild(box);
    statusModal.addEventListener('click', function (evt) {
      if (evt.target === statusModal && statusState !== 'checking') dismissStatusModal();
    });
    document.addEventListener('keydown', onStatusKeydown);
    document.body.appendChild(statusModal);
  }

  function onAddClick() {
    var button = el('[data-fish-add-button]');
    if (!button || button.classList.contains('btn--loading')) return;
    showMessage('[data-fish-add-error]', null);

    // Final clamp of everything visible, then sync the FULL selections map
    // (includes fish picked on other pages and pending removals). The gate
    // diffs against the live cart, so untouched cart lines stay put.
    var inputs = qtyInputs();
    for (var i = 0; i < inputs.length; i++) rememberInput(inputs[i], true);
    var desired = {};
    var any = false;
    var buying = false;
    for (var id in selections) {
      if (!Object.prototype.hasOwnProperty.call(selections, id)) continue;
      desired[id] = selections[id].q;
      any = true;
      if (selections[id].q > 0) buying = true;
    }
    if (!any) { updateSummary(); return; }

    // Over the online order cap: the button is disabled while over, but
    // guard the flow too (keyboard/programmatic activation).
    if (buying && overOrderLimit(selectionState().subtotal)) {
      showStatusModal('error', orderLimitMessage(), null);
      return;
    }

    // Required drive-time confirmation — checked before anything locks.
    var attest = document.querySelector('[data-fish-attest]');
    if (buying && attest && !attest.checked) {
      var attestMessage = strings.attestRequired || 'Please tick the drive-time confirmation before checking out.';
      showMessage('[data-fish-add-error]', attestMessage);
      showStatusModal('error', attestMessage, null, function () {
        var wrap = document.querySelector('[data-fish-attest-wrap]');
        if (wrap) wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }

    // Required reservation-policy acknowledgement — only gates when the policy
    // is actually rendered (blank copy = no checkbox = nothing to agree to).
    var agree = document.querySelector('[data-fish-agree]');
    if (buying && agree && !agree.checked) {
      var agreeMessage = strings.agreementRequired || 'Please tick the box to acknowledge the Live Fish Reservation & Cancellation Policy before checking out.';
      showMessage('[data-fish-add-error]', agreeMessage);
      showStatusModal('error', agreeMessage, null, function () {
        var agreeWrap = document.querySelector('[data-fish-agree-wrap]');
        if (agreeWrap) agreeWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }

    // Grass carp Texas permit (fish-permits.js): question answered, and a
    // TX=Yes order has its permit attached — checked before anything locks.
    var permits = window.PondKing.FishPermits;
    var permitFailed = buying && permits && permits.check ? permits.check() : null;
    if (permitFailed) {
      showMessage('[data-fish-add-error]', permitFailed.message);
      showStatusModal('error', permitFailed.message, null, function () {
        var panelEl = document.querySelector('[data-fish-grass-carp-panel]');
        if (panelEl) panelEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }

    button.classList.add('btn--loading');
    button.disabled = true;

    function unlock() {
      button.classList.remove('btn--loading');
      updateSummary(); // re-enables the button when quantities remain
    }

    if (!window.PondKing.Gate || !window.PondKing.Gate.syncFish) {
      unlock();
      showMessage('[data-fish-add-error]', strings.addError || 'Unable to update the cart.');
      return;
    }

    // Pure removal (nothing left to buy): no confirmation popup — sync and
    // land on the cart page (checkout would just bounce an empty cart).
    if (!buying) {
      window.PondKing.Gate.syncFish(desired, {
        redirect: null, // gate default (/cart)
        success: clearSelections,
        error: function (message) {
          unlock();
          showMessage('[data-fish-add-error]', message);
        },
        cancel: unlock
      });
      return;
    }

    // Buying: pre-checkout confirmation popup. Re-verify the pickup slot and
    // the inventory live, sync the cart, then hand the customer to checkout
    // with a heads-up to finish within 5 minutes.
    showStatusModal('checking',
      strings.confirmChecking || 'Hang tight — confirming inventory and your pickup time…');

    // A failed check: say what happened + what to do next, release the button,
    // and mirror the message into the bar (visible after the modal closes).
    function failCheck(message, nextStep, slotRelated) {
      unlock();
      if (message === (strings.tableAdjusted || 'Availability just changed — your quantities were updated.')) {
        // The error line carries this text — don't show it twice in the bar.
        showMessage('[data-fish-adjust-notice]', null);
      }
      showMessage('[data-fish-add-error]', message);
      showStatusModal('error', message, nextStep, function () {
        if (slotRelated) {
          var pickerEl = document.getElementById('PickupSlotPicker');
          if (pickerEl) pickerEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }

    var pickup = window.PondKing.FishPickup;
    var slotCheck = (pickup && pickup.present())
      ? pickup.validate() // fresh capacity + selection + attributes on cart
      : Promise.resolve(null);

    slotCheck
      .then(function (slotFailed) {
        if (slotFailed) {
          // "No pickup times this week" is a dead end — don't tell the
          // customer to choose one below or scroll them to an empty list.
          var emptyWeek = slotFailed.message === (strings.noSlotsThisWeek || 'No pickup times are available this week.');
          failCheck(slotFailed.message,
            emptyWeek ? null : (strings.confirmSlotNext || 'Choose an open pickup time below and try again.'),
            !emptyWeek);
          return;
        }

        // Live inventory pass: re-fetch the section; restoreSelections (fired
        // by the swap) re-clamps every visible quantity to fresh stock.
        var refreshed = (window.PondKing.FishRefresh && window.PondKing.FishRefresh.refreshNow)
          ? window.PondKing.FishRefresh.refreshNow()
          : Promise.resolve();

        return Promise.resolve(refreshed).then(function () {
          var reduced = false;
          for (var id in desired) {
            if (!Object.prototype.hasOwnProperty.call(desired, id)) continue;
            var now = Object.prototype.hasOwnProperty.call(selections, id) ? selections[id].q : 0;
            if (desired[id] > 0 && now < desired[id]) { reduced = true; break; }
          }

          // Re-read the (possibly re-clamped) selections as the confirmed order.
          var confirmed = {};
          var stillBuying = false;
          for (var cid in selections) {
            if (!Object.prototype.hasOwnProperty.call(selections, cid)) continue;
            confirmed[cid] = selections[cid].q;
            if (selections[cid].q > 0) stillBuying = true;
          }

          if (reduced || !stillBuying) {
            failCheck(strings.tableAdjusted || 'Availability just changed — your quantities were updated.',
              strings.confirmReview || 'Review the updated availability in your order and try again.', false);
            return;
          }

          return window.PondKing.Gate.fetchCart().then(function (cart) {
            // Mixed cart → the gate's "Clear cart & add" modal needs the
            // screen; ours comes back on success/failure.
            var fishType = (window.PondKingFish && window.PondKingFish.fishType) || 'Live Animals';
            var mixed = false;
            var items = cart.items || [];
            for (var i = 0; i < items.length; i++) {
              if (items[i].product_type !== fishType) { mixed = true; break; }
            }
            if (mixed) closeStatusModal();

            window.PondKing.Gate.syncFish(confirmed, {
              redirect: false, // this flow owns navigation
              success: function () {
                clearSelections();
                showStatusModal('success',
                  strings.confirmSuccess || "You're all set! Your fish and pickup time are confirmed. Please complete checkout within the next 5 minutes.");
                // Stored so closeStatusModal/pageshow/Go-now can cancel it —
                // a resumed bfcache timer must never re-fire this redirect.
                statusRedirectTimer = setTimeout(function () { window.location.href = '/checkout'; }, 4000);
              },
              error: function (message) {
                failCheck(message,
                  strings.confirmReview || 'Review the updated availability in your order and try again.', false);
              },
              cancel: function () {
                closeStatusModal();
                unlock();
              }
            });
          });
        });
      })
      .catch(function () {
        failCheck(strings.addError || 'Something went wrong. Please try again.',
          strings.confirmReview || 'Review your order and try again.', false);
      });
  }

  function init() {
    root = document.querySelector('[data-fish-table-root]');
    if (!root) return;

    if (!listenersBound) {
      listenersBound = true;

      document.addEventListener('input', function (evt) {
        if (!evt.target || !evt.target.hasAttribute || !evt.target.hasAttribute('data-fish-qty')) return;
        if (!root || !root.contains(evt.target)) return;
        rememberInput(evt.target, false);
        updateSummary();
      });

      // Final clamp when the customer leaves the field (change fires on blur).
      document.addEventListener('change', function (evt) {
        if (!evt.target || !evt.target.hasAttribute || !evt.target.hasAttribute('data-fish-qty')) return;
        if (!root || !root.contains(evt.target)) return;
        rememberInput(evt.target, true);
        updateSummary();
      });

      document.addEventListener('fish:refreshed', function (evt) {
        if (!evt.detail || evt.detail.nodeId !== 'FishAjaxContent') return;
        restoreSelections();
      });

      // Preset cards (fish-preset-picker.liquid) live outside root — one
      // delegated listener covers them and survives theme-editor re-renders.
      document.addEventListener('click', function (evt) {
        var btn = evt.target.closest ? evt.target.closest('[data-fish-preset]') : null;
        if (!btn || !root) return;
        var items = presetItemsFor(btn);
        if (items) applyPreset(items);
      });

      // Complying with a required tick box clears its error message.
      document.addEventListener('change', function (evt) {
        var target = evt.target;
        if (!target || !target.hasAttribute) return;
        var message = null;
        if (target.hasAttribute('data-fish-attest')) {
          message = strings.attestRequired || 'Please tick the drive-time confirmation before checking out.';
        } else if (target.hasAttribute('data-fish-agree')) {
          message = strings.agreementRequired || 'Please tick the box to acknowledge the Live Fish Reservation & Cancellation Policy before checking out.';
        }
        if (!message || !target.checked) return;
        var errorNode = el('[data-fish-add-error]');
        if (errorNode && errorNode.textContent === message) {
          showMessage('[data-fish-add-error]', null);
        }
      });

      // Theme editor re-renders the section DOM — re-grab root and rebind.
      document.addEventListener('shopify:section:load', function () {
        if (document.querySelector('[data-fish-table-root]')) init();
      });
    }

    var button = el('[data-fish-add-button]');
    if (button && button.dataset.fishBound !== '1') {
      button.dataset.fishBound = '1';
      button.addEventListener('click', onAddClick);
    }

    restoreSelections(); // fast paint from stored selections…
    seedFromCart().then(function () {
      restoreSelections(); // …then reconcile with what's actually in the cart

      // ?preset=<block id> deep link: only when the customer arrives with a
      // clean slate — never clobber selections or carted fish they already
      // have. One shot per page view.
      if (root.dataset.fishPresetApplied === '1') return;
      root.dataset.fishPresetApplied = '1';
      var handle;
      try { handle = new URLSearchParams(window.location.search).get('preset'); } catch (err) { return; }
      if (!handle || Object.keys(selections).length > 0) return;
      var btn = document.querySelector('[data-fish-preset="' + handle.replace(/"/g, '') + '"]');
      if (!btn) return;
      var items = presetItemsFor(btn);
      if (items) applyPreset(items);
    });
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  // bfcache back-nav (e.g. from checkout) restores the page mid-flight: the
  // button frozen in btn--loading and selections that were cleared on the way
  // out. Unlock and re-sync with reality.
  window.addEventListener('pageshow', function (evt) {
    if (!evt.persisted || !root) return;
    closeStatusModal(); // a bfcache snapshot may have frozen the popup open
    var button = el('[data-fish-add-button]');
    if (button) button.classList.remove('btn--loading');
    showMessage('[data-fish-add-error]', null);
    selections = loadStored();
    seedFromCart().then(restoreSelections);
  });

  window.PondKing.FishTable = {
    restoreSelections: restoreSelections,
    clearSelections: clearSelections,
    applyPreset: applyPreset
  };
})();
